<script setup lang="ts">
import { CheckCircleOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useCienciaEncerramentoLegalAi2 } from '~/composables/useCienciaEncerramentoLegalAi2';

interface Props {
  compromissoCodigos?: string[];
  compromisso?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const formRef = ref<any>();
const justificativaAnterior = ref<string | null>(null);

const model = reactive({
  observacao: '',
});

const { status, result, errorMessage, submit, stopPolling, setLoading } = useCienciaEncerramentoLegalAi2();

const isLoading = computed(() => status.value === 'loading');
const isSuccess = computed(() => status.value === 'success');
const isPartial = computed(() => status.value === 'partial');
const showForm = computed(() => !isSuccess.value && !isPartial.value);

const isLote = computed(() => {
  const totalCompromissos = props.compromissoCodigos?.length ?? 0;
  return totalCompromissos > 1;
});

onMounted(() => {
  // Busca justificativa do compromisso Atender anterior (dados do fluxo)
  if (props.compromisso?.fluxo?.compromissos) {
    const atenderCompromisso = props.compromisso.fluxo.compromissos
      .filter((c: any) => c.tipo === 351 && c.status === 2) // AtenderEncerramentoLegalAi2 concluído
      .sort((a: any, b: any) => new Date(b.dataConclusao).getTime() - new Date(a.dataConclusao).getTime())[0];

    if (atenderCompromisso?.data?.justificativa) {
      justificativaAnterior.value = atenderCompromisso.data.justificativa;
    }
  }
});

async function handleSubmit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  setLoading();

  const compromissoCodigos = props.compromissoCodigos?.length
    ? props.compromissoCodigos
    : props.compromisso
      ? [props.compromisso.codigo]
      : [];

  await submit({
    compromissoCodigos,
    observacao: model.observacao || undefined,
  });

  if (status.value === 'error') {
    notification.error({
      message: errorMessage.value || 'Tivemos um problema inesperado',
      duration: 10,
    });
  }
}

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <a-form
    v-if="showForm"
    ref="formRef"
    :model="model"
    autocomplete="off"
    layout="vertical"
  >
    <a-alert
      v-if="props.compromissoCodigos && props.compromissoCodigos.length > 1"
      type="warning"
      message="Você irá realizar uma alteração em lote"
      show-icon
      style="margin-bottom: 24px"
    />

    <!-- Justificativa do Atender (read-only) -->
    <a-descriptions
      v-if="justificativaAnterior"
      :column="1"
      bordered
      style="margin-bottom: 24px"
    >
      <a-descriptions-item label="Justificativa (impossibilidade de encerrar)">
        {{ justificativaAnterior }}
      </a-descriptions-item>
    </a-descriptions>

    <a-form-item
      label="Observação"
      name="observacao"
    >
      <a-textarea v-model:value="model.observacao" :rows="4" />
    </a-form-item>

    <a-form-item>
      <a-button
        type="primary"
        :disabled="isLoading"
        :loading="isLoading"
        size="large"
        @click.prevent="handleSubmit"
      >
        <template #icon>
          <SaveOutlined />
        </template>
        {{ isLoading ? 'Salvando...' : 'Salvar' }}
      </a-button>
    </a-form-item>
  </a-form>

  <!-- Resultado de sucesso (simples) -->
  <a-result
    v-if="isSuccess && !isLote"
    status="success"
    title="Ciência registrada com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado de sucesso (lote) -->
  <a-result
    v-if="isSuccess && isLote"
    status="success"
    title="Ciências registradas com sucesso"
    :sub-title="`${result?.totalSucesso || 0} ciência(s) processada(s) com sucesso`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 300px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
            </div>
            <CheckCircleOutlined style="color: #52c41a; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado parcial (lote com falhas) -->
  <a-result
    v-if="isPartial"
    status="warning"
    title="Processamento parcial"
    :sub-title="`${result?.totalSucesso || 0} sucesso(s), ${result?.totalFalha || 0} falha(s)`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 450px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left; flex: 1;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
              <span v-if="!item.sucesso" style="color: #ff4d4f; margin-left: 8px; font-size: 12px;">
                - {{ item.erro }}
              </span>
            </div>
            <CheckCircleOutlined v-if="item.sucesso" style="color: #52c41a; margin-left: 8px;" />
            <ExclamationCircleOutlined v-else style="color: #ff4d4f; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>
</template>
